import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import * as Api from "@/api";
import { NEW_PBX } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumeric from "@/design/forms/InputNumeric";
import InputText from "@/design/forms/InputText";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import { viBuilding } from "@sippy-platform/valkyrie";

import { Button, CardHeader, Dialog, Grid, Stack } from "@mui/material";
import PropTypes from "prop-types";

import pbxReducer from "./pbxReducer";

function AddPbx({ navigateOnCreate, closeDialog, ...props }) {
  const navigate = useNavigate();

  const partnerStore = useSelector((state) => state.backstage.partners);
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    function getData() {
      Promise.resolve(Api.getPartners());
    }

    if (auth.accessLevel === 0) getData();
  }, [auth.accessLevel]);

  const {
    value: pbx,
    dispatch: pbxDispatch,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: {
      ...NEW_PBX,
      partnerId: auth.partner?.id ?? "",
    },
    mode: SaveMode.MANUAL,
    reducer: pbxReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      partnerId: number().required({ message: "Partner is required." }),
      maxSippeers: number().required({ message: "Maximum users is required." }),
      maxConcurrentLines: number().required({ message: "Maximum lines is required." }),
      zip: string().required({ message: "ZIP is required." }),
    }),
    onSubmit: () => Api.postPbx(pbx),
    onSuccess: (response) => {
      closeDialog(false);

      if (navigateOnCreate) {
        pbxDispatch({ type: "id", value: response.data.pbx.id });
        navigate(`/backstage/pbxes/${response.data.pbx.context}`);
      }

      Promise.resolve(Api.getPbxes());
    },
  });

  // Handle changes
  const formHandler = (property, value) => {
    pbxDispatch({ type: property, value });
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => closeDialog(false)}
      maxWidth="md"
      slotProps={{ paper: { id: "addPbxDialog" } }}
    >
      <HeaderCard>
        <CardHeader avatar={<Avatar text={pbx.name} icon={viBuilding} />} title={pbx.name || "New PBX"} />
      </HeaderCard>
      <Stack
        sx={{
          gap: 2,
          mt: 2,
        }}
      >
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={pbx.name} error={errors} />
          </Grid>
          {auth.accessLevel === 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputAutocomplete
                required
                id="partnerId"
                label="Partner"
                onChange={formHandler}
                value={pbx.partnerId}
                selectValue="id"
                selects={partnerStore.list}
                getOptionLabel={(option) => option.name}
                error={errors}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="maxSippeers"
              label="Maximum users"
              onChange={formHandler}
              min={1}
              value={pbx.maxSippeers}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="maxConcurrentLines"
              label="Maximum lines"
              onChange={formHandler}
              min={1}
              value={pbx.maxConcurrentLines}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="zip" label="ZIP" onChange={formHandler} value={pbx.zip} error={errors} />
          </Grid>
        </FormGroup>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            gap: 1,
            containerType: "inline-size",
          }}
        >
          <Button onClick={() => closeDialog(false)} variant="outlined">
            Cancel
          </Button>
          <SaveIndicator formState={formState} errors={allErrors} save={save} />
        </Stack>
      </Stack>
    </Dialog>
  );
}

AddPbx.propTypes = {
  closeDialog: PropTypes.func,
  open: PropTypes.bool,
  navigateOnCreate: PropTypes.bool,
};

export default AddPbx;
